<script setup>

import {ref, nextTick, onMounted, computed} from 'vue';
import Validation from "@/classes/global/Validation";
import TextArea from "@/components/common/input/TextArea"
import {useCreateSubstringText} from "@/composables/common/useCreateSubstringText";
import DOMPurify from 'dompurify';

const popupSelectResults = ref(props.popupResults);
const showResults = ref(false);
const closeTimer = ref(null);
const closeTime = ref(600);
const menuFullOpenHeight = ref({type: Number, default: 0})

// HTML Refs
const inputRef = ref();
const selectRef = ref();
const ulRef = ref();

const emits = defineEmits(['update:modelValue', 'update:validationFail'])

const props = defineProps({

  modelValue: {},

  placeholder: {type: String, default: 'Text Area...'},
  clearable: {type: Boolean, default: true},
  validationFail: {type: Object, default: new Validation()},
  validationText: {type: Array, default: ['Required']},
  popupResults: {type: Boolean, default: false},
  disable: {type: Boolean, default: false},
  width: {type: Number, default: 450},
  textAreaWidth: {type: Number},
  label: {type: String, default: ''},
  labelTop: {type: Boolean, default: false},
  labelWidth: Number,
  requiredField: {type: Boolean, default: false},
  maxLength: {type: Number, default: 50},
  labelColor: {type: String, default: '#000'},

})

const requiredFieldColor = computed(() => getRequiredFieldColor());

const displayedValue = computed( {

  get() {

    return useCreateSubstringText(DOMPurify.sanitize(props.modelValue), props.maxLength);
  },

  set(val) {

    return DOMPurify.sanitize(val);
  }

});

const handleTextAreaWidth = computed(() => {

  if(!props.textAreaWidth) return props.width -7

  return props.textAreaWidth -7;
})

const vFocus = {
  mounted: (el) => {
    el.firstChild.focus()
  }
}


function getRequiredFieldColor() {
  if (props.requiredField) {
    return 'background-color: #ffeda9';
  }
  return '';
}

// End Computed

function getSelectResultContainerPosition(popupResults) {

  nextTick(() => {

    let inputElement = inputRef.value
    let selectDrop = selectRef.value;

    let inputTopPosition = inputElement.$el.children[1].getBoundingClientRect().top;
    let inputLeftPosition = inputElement.$el.children[1].getBoundingClientRect().left;
    let inputWidth = inputElement.$el.children[1].getBoundingClientRect().width - 2;


    if (selectDrop) {

      let selectHeight = selectDrop.clientHeight + 29;
      menuFullOpenHeight.value = selectHeight

      if (!popupResults) {

        selectDrop.style.cssText = `top: ${inputTopPosition + "px"}; left: ${inputLeftPosition + "px"}; width: ${inputWidth + "px"} `
      } else {

        selectDrop.style.cssText = `top: ${inputTopPosition - selectHeight + "px"}; left: ${inputLeftPosition + "px"}; width: ${inputWidth + "px"} `
      }

    }

  })

}

function reEnterSelect() {

  document.removeEventListener('wheel', closeResults)

  clearTimeout(closeTimer.value);

}

function autoCloseSelectResults(e) {

  //Measure the mouse position within the menu boundaries - close if mouse is out of bounds
  if (showResults.value) {

    let selectDrop = selectRef.value;

    let selectTop = selectDrop.offsetTop;
    let selectLeft = selectDrop.offsetLeft;
    let selectWidth = selectDrop.getBoundingClientRect().width;
    let selectHeight = selectDrop.clientHeight + 29;

    // Slight difference in value adjustment between popup and normal to stop auto close behaviour bug
    let selectBottom = selectTop + selectHeight - 32;
    let selectBottomPopup = selectTop + selectHeight - 29;

    if (!popupSelectResults.value) {

      if (e.clientX < selectLeft || e.clientX > selectLeft + selectWidth - 10 || e.pageY - window.scrollY < selectTop
          || e.pageY > selectBottom) {

        window.addEventListener("scroll", closeResults);
        document.addEventListener("wheel", closeResults);
        window.addEventListener('click', closeResults)

        // Only close if the menu is fully open
        if (selectHeight === menuFullOpenHeight.value) {

          clearTimeout(closeTimer.value)
          closeTimer.value = setTimeout(() => closeResults(), closeTime.value);

        }
      }

    } else {

      if (e.clientX < selectLeft || e.clientX > selectLeft + selectWidth - 10 || e.pageY < selectTop
          || e.pageY > selectBottomPopup) {

        window.addEventListener("scroll", closeResults);
        document.addEventListener("wheel", closeResults);

        if (selectHeight === menuFullOpenHeight.value) {

          clearTimeout(closeTimer.value)
          closeTimer.value = setTimeout(() => closeResults(), closeTime.value);

        }

      }

    }

  }
}

function setupClick(e) {

  // Make the select popup if the input is so far down the screen

  popupSelectResults.value = false;
  let lowerScreenArea = window.outerHeight / 1.3

  if (e.screenY > lowerScreenArea) {

    popupSelectResults.value = true;
  }

  showResults.value = !showResults.value

  getSelectResultContainerPosition(popupSelectResults.value);

}

function stopScroll(e) {
  if (showResults.value) e.preventDefault();
}


function closeResults() {

  showResults.value = false;
  window.removeEventListener('scroll', closeResults)
  document.removeEventListener('wheel', closeResults)
  window.removeEventListener('click', closeResults)
}


function handleClear() {
  showResults.value = false;
  emits("update:modelValue", '');
  emits('update:validationFail', new Validation(0, false));
}

function emitValues(value) {

  emits('update:validationFail', new Validation());

  let sanitizedValue =  DOMPurify.sanitize(value);
  if(sanitizedValue !== value) return inputRef.value.value = '';
  emits('update:modelValue', value);
}

</script>

<template>
  <div id="Vue-3-component-filtered-dropdown-menu" style="position: relative;">
    <div class="value-status" :class="{'label-margin' : label}">
    </div>
    <TextInput
        id="inputElement"
        ref="inputRef"
        :label="label"
        :label-top="labelTop"
        :labelWidth="labelWidth"
        :labelColor="labelColor"
        :requiredField="requiredField"
        :title="props.modelValue"
        :readonly="true"
        v-model="displayedValue"
        :validation-fail="validationFail"
        :validation-text="validationText"
        @click.self="setupClick"
        @wheel="stopScroll"
        @cleared="handleClear"
        :clearable="clearable"
        @mouseleave="autoCloseSelectResults"
        :disable="disable"
        :width="width"
        :placeholder="props.placeholder">
    </TextInput>
    <div tabindex="0" id="select-results" ref="selectRef" @mouseleave="autoCloseSelectResults"
         @mouseenter="reEnterSelect" v-if="showResults === true"
         :class="{'select-item-container' : !popupSelectResults, 'select-item-container-popup' : popupSelectResults}">
      <TextArea
          @input="emitValues($event)"
          v-model="props.modelValue"
          v-focus
          :height="140"
          :width="handleTextAreaWidth">
      </TextArea>
    </div>
  </div>
</template>

<style scoped>

/*This was added to prevent other imported css from frameworks breaking the select */
/* May need to be addressed if this causes issues with sharing framework controls */
* {
  box-sizing: revert !important;
}

::-webkit-scrollbar {
  width: 12px;
}

::-webkit-scrollbar-track {
  background-color: #f6f6f6;
}

::-webkit-scrollbar-thumb {
  background: #b3b3b3;
  border-radius: 2px;
}

ul {
  font-size: 12px;
  padding: 0;
}

.input-icon-container {
  position: absolute;
  cursor: pointer;
  right: 2px;
  top: 3px;
  padding-left: 6px;
  height: 20px;
  width: 14px;
  margin-right: 2px;
  background: #d8d8d8;
  border-radius: 4px;
  font-size: 14px;
  line-height: normal;

}

.input-icon-container:hover {
  background-color: #d8d8d8;
  border-radius: 4px;
}

.input-clear-btn {
  position: absolute;
  right: 0;
  /*top: -4px;*/
  padding: 8px;
  z-index: 99;
  font-weight: bolder;
  cursor: pointer;
}

.input-clear-container {
  position: absolute;
  cursor: pointer;
  right: 2px;
  top: 3px;
  padding-left: 6px;
  height: 20px;
  width: 14px;
  margin-right: 2px;
  font-size: 14px;
  line-height: normal;
}

.input-cross {
  position: absolute;
  font-family: Arial;
  transform: rotate(45deg);
  color: #566ea0;
  font-size: 20px;
  right: 5px;
  /*top: 6px;*/
  background: none;
  user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
}

.input-clear-btn:hover {
  opacity: 0.5;
  transition: 0.3s;
}

/*.input-arrow-down {*/
/*  position: absolute;*/
/*  top: 7px;*/
/*  right: 3px;*/
/*  content: "";*/
/*  border-left: 7px solid transparent;*/
/*  border-right: 7px solid transparent;*/
/*  border-top: 8px solid #393939;*/
/*}*/

.select-item-container {
  top: 0;
  position: fixed;
  margin-top: 26px;
  z-index: 999999;
}

.select-item-container-popup {
  position: fixed;
  margin-top: 36px;
  z-index: 999999;
}


.value-status {
  position: absolute;
  left: 5px;
  top: -6px;
  z-index: 99;
  background-color: #ffffff;
  font-size: 11px;
  color: #42b983;
  padding-left: 4px;
  padding-right: 4px;
  border-radius: 8px;
}

.label-margin {
  left: 115px;
}



</style>
